import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	BgMaxPriceResult,
	ErrorResponse,
	findBgMaxPrice,
	ProductUpdate,
	updateBgMaxPrice as apiUpdateBgMaxPrice,
} from '~/Api/User/ProductGroup/BgMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionTypes } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export const fetchBgMaxPrice =
	(groupId: string): ThunkAction<Promise<BgMaxPriceResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findBgMaxPrice, userId, groupId));
		dispatch({ type: ActionTypes.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				groupId,
				bgMaxPrice: response?.bgMaxPrice ?? null,
				request,
				type: ActionTypes.receive,
			})
		);
	};

export const updateBgMaxPrice =
	(
		groupId: string,
		basketVersion: string,
		packageSize: number | null,
		historicProductDataViewDate: Date | null,
		products: ProductUpdate[]
	): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const request = updateQueue.add(() =>
			dispatch(immediatelyUpdateBgMaxPrice(groupId, basketVersion, packageSize, historicProductDataViewDate, products))
		);
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });
		return request;
	};

export const immediatelyUpdateBgMaxPrice =
	(
		groupId: string,
		basketVersion: string,
		packageSize: number | null,
		historicProductDataViewDate: Date | null,
		products: ProductUpdate[]
	): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(
				invokeApi(
					apiUpdateBgMaxPrice,
					userId,
					groupId,
					basketVersion,
					packageSize,
					historicProductDataViewDate,
					products
				)
			)
		);
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				groupId,
				bgMaxPrice: response?.bgMaxPrice ?? null,
				request,
				type: ActionTypes.receiveUpdate,
			})
		);
	};

export const handleBgMaxPriceUpdateResponse =
	<Response>(response: Response, groupId: string): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}
		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchBgMaxPrice(groupId)).catch(handleError);
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchBgMaxPrice(groupId)).catch(handleError);
		}
		dispatch(handleError(response));
	};

export const pruneBgMaxPrice = (groupId: string): Action => ({
	type: ActionTypes.prune,
	groupId,
});

export const scheduleBgMaxPriceUpdate =
	(
		groupId: string,
		basketVersion: string,
		packageSize: number | null,
		historicProductDataViewDate: Date | null,
		products: ProductUpdate[]
	): ThunkAction<Promise<void>, Action> =>
	async (dispatch) =>
		new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateBgMaxPrice(groupId, basketVersion, packageSize, historicProductDataViewDate, products)));
			}, 2000);

			dispatch({
				basketVersion,
				groupId,
				packageSize,
				historicProductDataViewDate,
				products,
				timeoutId,
				type: ActionTypes.scheduleUpdate,
			});
		});

export const commitBgMaxPriceUpdate =
	(groupId: string): ThunkAction<Promise<void>, Action> =>
	async (dispatch, getState) => {
		const { update } = getState().bgMaxPrice;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, basketVersion, packageSize, historicProductDataViewDate, products } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateBgMaxPrice(groupId, basketVersion, packageSize, historicProductDataViewDate, products));
		}
	};

export const handleCommitError =
	<Error>(error: Error): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
